import instance from '@instance';

// Team leagues /event/team-calendar accepts that also have a team page here.
const SCHEDULE_LEAGUES = [
  'mlb',
  'nba',
  'nhl',
  'nfl',
  'mls',
  'mls-major-league-soccer',
  'ncaa-mens-football',
  'ncaa-mens-basketball',
  'english-premier-league',
  'la-liga',
  'italian-serie-a',
  'bundesliga',
  'ligue-1',
] as const;

export type ScheduleLeague = (typeof SCHEDULE_LEAGUES)[number];

// URL slugs the API names differently. ncaaf/ncaab stay on the list: their team slugs skip canonicalization.
const SCHEDULE_LEAGUE_ALIASES: Record<string, ScheduleLeague> = {
  'premier-league': 'english-premier-league',
  'serie-a': 'italian-serie-a',
};

export interface TeamRef {
  id: number;
  slug: string;
  name: string;
  shortName: string;
}

export interface TeamGame {
  eventId: number;
  name: string;
  occursAt: string;
  occursAtLocal: string;
  date: string;
  isTbd: boolean;
  isHome: boolean;
  opponent: TeamRef;
  venue: { id: number; name: string; location: string | null };
}

export interface TeamCalendarGame extends TeamGame {
  fromPrice: number | null;
}

interface TeamScheduleResponse {
  team: TeamRef & { league: ScheduleLeague };
  priceSource: 'unavailable' | 'tevo';
}

export interface TeamCalendarResponse extends TeamScheduleResponse {
  startDate: string;
  endDate: string;
  currency: 'USD';
  opponents: TeamRef[];
  games: TeamCalendarGame[];
}

// Quote fields are null while priceSource is 'unavailable'.
export interface TeamPackage extends TeamGame {
  price: { perTraveler: number | null; currency: 'USD' };
  includes: {
    hotel: { name: string | null; nights: number | null };
    ticketTier: 'Upper level' | 'Lower level' | 'Club level' | null;
    taxesAndFees: boolean;
  };
}

export interface TeamPackagesResponse extends TeamScheduleResponse {
  packages: TeamPackage[];
}

export interface TeamScheduleQuery {
  league: ScheduleLeague;
  teamSlug: string;
}

export interface TeamCalendarQuery extends TeamScheduleQuery {
  startDate?: string;
  endDate?: string;
  onlyHomeGames?: boolean;
  opponentId?: number;
}

export const getScheduleLeague = (league: string): ScheduleLeague | null => {
  const slug = league.toLowerCase();
  return (
    SCHEDULE_LEAGUE_ALIASES[slug] ??
    SCHEDULE_LEAGUES.find((value) => value === slug) ??
    null
  );
};

const requestTeamSchedule = async <ResponseType extends TeamScheduleResponse>(
  path: string,
  params: URLSearchParams
): Promise<ResponseType> => {
  const response = await instance.server(`${path}?${params}`, {
    method: 'GET',
    cache: 'no-store',
  });
  if (!response.ok) throw new Error('Unable to load team events');

  const data = await response.json();
  if (
    !data?.team?.name ||
    !['unavailable', 'tevo'].includes(data.priceSource)
  ) {
    throw new Error('Invalid team events response');
  }
  return data;
};

export const getTeamCalendar = async ({
  league,
  teamSlug,
  startDate,
  endDate,
  onlyHomeGames,
  opponentId,
}: TeamCalendarQuery): Promise<TeamCalendarResponse> => {
  const params = new URLSearchParams({ league, teamSlug });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (onlyHomeGames !== undefined)
    params.set('onlyHomeGames', String(onlyHomeGames));
  if (opponentId !== undefined) params.set('opponentId', String(opponentId));

  const data = await requestTeamSchedule<TeamCalendarResponse>(
    '/event/team-calendar',
    params
  );
  if (
    !Array.isArray(data.games) ||
    !Array.isArray(data.opponents) ||
    !data.startDate ||
    !data.endDate ||
    data.currency !== 'USD'
  ) {
    throw new Error('Invalid team calendar response');
  }
  return data;
};

export const getTeamPackages = async ({
  league,
  teamSlug,
  limit,
}: TeamScheduleQuery & { limit?: number }): Promise<TeamPackagesResponse> => {
  const params = new URLSearchParams({ league, teamSlug });
  if (limit !== undefined) params.set('limit', String(limit));

  const data = await requestTeamSchedule<TeamPackagesResponse>(
    '/event/team-packages',
    params
  );
  if (!Array.isArray(data.packages))
    throw new Error('Invalid team packages response');
  return data;
};
